import {Badge, Box, Grid, Multiselect, Pagination, Table, TableProps} from '@amzn/awsui-components-react';
import React, {useMemo, useState} from 'react';
import {useCollection} from '@amzn/awsui-collection-hooks';
import {InboundBridgeChannelPlanActual, NodeFilterData} from './index';
import * as awsui from '@amzn/awsui-design-tokens/polaris';

const TABLE_PAGE_SIZE = 30;

function buildColumnDefinitions(channels: string[]): TableProps.ColumnDefinition<ChannelPlanActualRecord>[] {
    const fmt = (val: number) => val.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0});

    const deltaCell = (val: number) => {
        const color = val < 0 ? awsui.colorChartsRed600 : val > 0 ? awsui.colorChartsTeal600 : 'inherit';
        return <span style={{color, fontWeight: 500}}>{fmt(val)}</span>;
    };

    const dateCol: TableProps.ColumnDefinition<ChannelPlanActualRecord> = {
        id: 'date',
        header: 'Date',
        cell: (item) => {
            if (item.isWeekRow) return item.date;
            const d = new Date(item.date + 'T00:00:00');
            const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
            return `${item.date} (${dow})`;
        },
        isRowHeader: true,
    };

    const planTotalCol: TableProps.ColumnDefinition<ChannelPlanActualRecord> = {
        id: 'plan_total',
        header: (
            <span>
                Plan <Badge color="blue">Total</Badge>
            </span>
        ),
        cell: (item) => <b>{fmt(item.planTotal)}</b>,
    };

    const actualTotalCol: TableProps.ColumnDefinition<ChannelPlanActualRecord> = {
        id: 'actual_total',
        header: (
            <span>
                Actual <Badge color="blue">Total</Badge>
            </span>
        ),
        cell: (item) => <b>{fmt(item.actualTotal)}</b>,
    };

    const deltaTotalCol: TableProps.ColumnDefinition<ChannelPlanActualRecord> = {
        id: 'delta_total',
        header: (
            <span>
                Delta <Badge color="blue">Total</Badge>
            </span>
        ),
        cell: (item) => {
            const val = item.deltaTotal;
            const color = val < 0 ? awsui.colorChartsRed600 : val > 0 ? awsui.colorChartsTeal600 : 'inherit';
            return <b style={{color}}>{fmt(val)}</b>;
        },
    };

    const channelPlanCols: TableProps.ColumnDefinition<ChannelPlanActualRecord>[] = channels.map((ch) => ({
        id: `plan_${ch}`,
        header: ch,
        cell: (item) => fmt(item.byChannel[ch]?.plan ?? 0),
    }));

    const channelActualCols: TableProps.ColumnDefinition<ChannelPlanActualRecord>[] = channels.map((ch) => ({
        id: `actual_${ch}`,
        header: ch,
        cell: (item) => fmt(item.byChannel[ch]?.actual ?? 0),
    }));

    const channelDeltaCols: TableProps.ColumnDefinition<ChannelPlanActualRecord>[] = channels.map((ch) => ({
        id: `delta_${ch}`,
        header: ch,
        cell: (item) => deltaCell(item.byChannel[ch]?.delta ?? 0),
    }));

    return [dateCol, planTotalCol, ...channelPlanCols, actualTotalCol, ...channelActualCols, deltaTotalCol, ...channelDeltaCols];
}

export function InboundBridgeChannelPlanActualTable({
    bridgeData,
    isLoading,
}: {
    bridgeData: InboundBridgeChannelPlanActual[];
    selectedFilter: NodeFilterData | undefined;
    isLoading: boolean;
}) {
    const [tableSelectedWeeks, setTableSelectedWeeks] = useState<string[]>([]);
    const [expandedItems, setExpandedItems] = useState<ChannelPlanActualRecord[]>([]);

    const tableEmpty = (
        <Box textAlign="center" color="inherit">
            <b>No matching data</b>
        </Box>
    );

    const {rows, channels} = useMemo(() => processChannelPlanActual(bridgeData), [bridgeData]);
    const parentRows = useMemo(() => rows.filter((r) => r.isWeekRow), [rows]);
    const columnDefs = useMemo(() => buildColumnDefinitions(channels), [channels]);

    const {items, collectionProps, paginationProps} = useCollection(parentRows, {
        filtering: {
            empty: tableEmpty,
            noMatch: tableEmpty,
            filteringFunction(item) {
                return tableSelectedWeeks.length === 0 || tableSelectedWeeks.includes(item.date);
            },
        },
        pagination: {pageSize: TABLE_PAGE_SIZE},
    });

    return (
        <Table
            data-testid="channel-plan-actual-table"
            variant="embedded"
            items={items}
            expandableRows={{
                getItemChildren: (item) => rows.filter((r) => r.parentId === item.id),
                isItemExpandable: (item) => item.isWeekRow,
                expandedItems,
                onExpandableItemToggle: ({detail}) => {
                    setExpandedItems((prev) =>
                        detail.expanded ? [...prev, detail.item] : prev.filter((i) => i.id !== detail.item.id)
                    );
                },
            }}
            stickyHeader
            stripedRows
            empty={tableEmpty}
            pagination={<Pagination {...paginationProps} />}
            filter={
                <Grid gridDefinition={[{colspan: 4}]}>
                    <Multiselect
                        data-testid="table-week-multiselect"
                        selectedOptions={tableSelectedWeeks.map((w) => ({label: w, value: w}))}
                        onChange={({detail}) => setTableSelectedWeeks(detail.selectedOptions.map((o) => o.value!))}
                        options={parentRows.map((r) => ({label: r.date, value: r.date}))}
                        tokenLimit={5}
                        filteringType="auto"
                        placeholder="Filter by week"
                    />
                </Grid>
            }
            columnDefinitions={columnDefs}
            loading={isLoading}
            enableKeyboardNavigation
            renderAriaLive={({firstIndex, lastIndex, totalItemsCount, visibleItemsCount}) =>
                `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}, ${visibleItemsCount} visible`
            }
        />
    );
}

function processChannelPlanActual(records: InboundBridgeChannelPlanActual[]): {
    rows: ChannelPlanActualRecord[];
    channels: string[];
} {
    // Collect all unique channels
    const channelSet = new Set<string>();
    records.forEach((r) => channelSet.add(r.channel));
    const channels = Array.from(channelSet).sort();

    // Aggregate per date + channel
    const dateChannelMap = new Map<string, Map<string, {plan: number; actual: number}>>();

    records.forEach((record) => {
        if (!dateChannelMap.has(record.date)) dateChannelMap.set(record.date, new Map());
        const chMap = dateChannelMap.get(record.date)!;
        if (!chMap.has(record.channel)) chMap.set(record.channel, {plan: 0, actual: 0});
        const entry = chMap.get(record.channel)!;
        if (record.name === 'Plan') entry.plan += record.value;
        else if (record.name === 'Actual') entry.actual += record.value;
    });

    // Group dates by week Sunday
    const weekMap = new Map<string, string[]>();
    dateChannelMap.forEach((_, date) => {
        const sunday = getSundayOfWeek(date);
        if (!weekMap.has(sunday)) weekMap.set(sunday, []);
        weekMap.get(sunday)!.push(date);
    });

    const rows: ChannelPlanActualRecord[] = [];
    let idCounter = 0;
    const sortedSundays = Array.from(weekMap.keys()).sort();

    sortedSundays.forEach((sunday) => {
        const dates = weekMap.get(sunday)!.sort();
        const parentId = `week-${idCounter++}`;

        // Accumulate week-level totals per channel
        const weekByChannel: Record<string, {plan: number; actual: number; delta: number}> = {};
        channels.forEach((ch) => (weekByChannel[ch] = {plan: 0, actual: 0, delta: 0}));

        const children: ChannelPlanActualRecord[] = dates.map((date) => {
            const chMap = dateChannelMap.get(date)!;
            const byChannel: Record<string, {plan: number; actual: number; delta: number}> = {};
            let planTotal = 0;
            let actualTotal = 0;

            channels.forEach((ch) => {
                const {plan, actual} = chMap.get(ch) ?? {plan: 0, actual: 0};
                const delta = actual - plan;
                byChannel[ch] = {plan, actual, delta};
                planTotal += plan;
                actualTotal += actual;
                weekByChannel[ch].plan += plan;
                weekByChannel[ch].actual += actual;
                weekByChannel[ch].delta += delta;
            });

            return {
                id: `day-${idCounter++}`,
                parentId,
                isWeekRow: false,
                date,
                planTotal,
                actualTotal,
                deltaTotal: actualTotal - planTotal,
                byChannel,
            };
        });

        const weekPlanTotal = channels.reduce((s, ch) => s + weekByChannel[ch].plan, 0);
        const weekActualTotal = channels.reduce((s, ch) => s + weekByChannel[ch].actual, 0);

        rows.push({
            id: parentId,
            parentId: null,
            isWeekRow: true,
            date: `Week of ${sunday}`,
            planTotal: weekPlanTotal,
            actualTotal: weekActualTotal,
            deltaTotal: weekActualTotal - weekPlanTotal,
            byChannel: weekByChannel,
        });

        rows.push(...children);
    });

    return {rows, channels};
}

function getSundayOfWeek(dateStr: string): string {
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getUTCDay();
    date.setUTCDate(date.getUTCDate() - day);
    return date.toISOString().split('T')[0];
}

export type ChannelPlanActualRecord = {
    id: string;
    parentId: string | null;
    isWeekRow: boolean;
    date: string;
    planTotal: number;
    actualTotal: number;
    deltaTotal: number;
    byChannel: Record<string, {plan: number; actual: number; delta: number}>;
};
